/**
 * CollaborationService handles real-time updates for collaborative forms
 * Uses polling strategy to fetch latest form updates from backend
 */

import { authApi, type ApiResponse, type GenericErrorResponse } from "./authService";

export interface FormUpdate<T = unknown> {
  formType: 'checklist' | 'variables' | 'assessment' | 'calculator';
  formId: string;
  data: T;
  timestamp: string;
  updatedBy?: {
    userId: string;
    userName: string;
    email?: string;
  };
  version?: number;
}

export interface FormUpdateResponse<T = unknown> {
  hasUpdates: boolean;
  updates?: FormUpdate<T>;
  lastSyncTime: string;
}

interface CollaborationUpdatesResponse<T = unknown> extends ApiResponse {
  updates?: FormUpdate<T>;
  lastSyncTime?: string;
}

interface CollaborationFormFieldsResponse<T = unknown> extends ApiResponse {
  data?: T;
  form?: T;
  formData?: T;
  fields?: T;
}

class CollaborationService {
  private pollingIntervals: Map<string, NodeJS.Timeout> = new Map();
  private lastSyncTimes: Map<string, string> = new Map();
  private lastPollTimes: Map<string, number> = new Map();
  private activePollRequests: Map<string, boolean> = new Map();
  private readonly MIN_POLL_INTERVAL = 3000; // Minimum 3 seconds between polls

  constructor() {}

  /**
   * Get debug information about active polling
   */
  getDebugInfo() {
    return {
      activePolls: Array.from(this.pollingIntervals.keys()),
      pollCount: this.pollingIntervals.size,
    };
  }

  /**
   * Start polling for form updates
   * @param formType - Type of form being monitored
   * @param formId - Unique identifier for the form instance
   * @param callback - Function to call when updates are received
   * @param interval - Polling interval in milliseconds (default: 5000)
   * @returns Cleanup function to stop polling
   */
  startPolling<T = unknown>(
    formType: FormUpdate['formType'],
    formId: string,
    callback: (update: FormUpdate<T>) => void,
    interval = 5000
  ): () => void {
    const key = `${formType}_${formId}`;

    // Prevent duplicate polling for the same form
    if (this.pollingIntervals.has(key)) {
      console.warn(`Polling already active for ${key}. Stopping previous instance.`);
      this.stopPolling(formType, formId);
    }

    // Enforce minimum interval to prevent excessive API calls
    const safeInterval = Math.max(interval, this.MIN_POLL_INTERVAL);

    if (interval < this.MIN_POLL_INTERVAL) {
      console.warn(`Polling interval increased from ${interval}ms to ${this.MIN_POLL_INTERVAL}ms to prevent excessive API calls`);
    }

    // Set initial sync time
    if (!this.lastSyncTimes.has(key)) {
      this.lastSyncTimes.set(key, new Date().toISOString());
    }

    console.log(`[CollaborationService] Starting polling for ${key} with ${safeInterval}ms interval`);

    // Start polling
    const intervalId = setInterval(async () => {
      const now = Date.now();
      const lastPoll = this.lastPollTimes.get(key) || 0;

      // Additional safeguard: skip if last poll was too recent
      if (now - lastPoll < this.MIN_POLL_INTERVAL) {
        console.warn(`[CollaborationService] Skipping poll for ${key} - too soon since last poll`);
        return;
      }

      if (this.activePollRequests.get(key)) {
        console.warn(`[CollaborationService] Skipping poll for ${key} - request still in flight`);
        return;
      }

      this.activePollRequests.set(key, true);
      this.lastPollTimes.set(key, now);

      try {
        const updates = await this.fetchFormUpdates<T>(formType, formId);
        if (updates.hasUpdates && updates.updates) {
          callback(updates.updates);
          // Update last sync time
          this.lastSyncTimes.set(key, updates.lastSyncTime);
        }
      } catch (error) {
        console.error(`[CollaborationService] Failed to fetch form updates for ${key}:`, error);
      } finally {
        this.activePollRequests.delete(key);
      }
    }, safeInterval);

    this.pollingIntervals.set(key, intervalId);

    // Return cleanup function
    return () => this.stopPolling(formType, formId);
  }

  /**
   * Stop polling for a specific form
   */
  stopPolling(formType: FormUpdate['formType'], formId: string): void {
    const key = `${formType}_${formId}`;
    const intervalId = this.pollingIntervals.get(key);
    if (intervalId) {
      console.log(`[CollaborationService] Stopping polling for ${key}`);
      clearInterval(intervalId);
      this.pollingIntervals.delete(key);
      this.lastPollTimes.delete(key);
      this.activePollRequests.delete(key);
    }
  }

  /**
   * Stop all active polling
   */
  stopAllPolling(): void {
    console.log(`[CollaborationService] Stopping all polling (${this.pollingIntervals.size} active)`);
    this.pollingIntervals.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    this.pollingIntervals.clear();
    this.lastPollTimes.clear();
    this.activePollRequests.clear();
  }

  /**
   * Fetch form updates from the backend
   * This is a mock implementation - replace with actual API call
   */
  async fetchFormUpdates<T = unknown>(
    formType: FormUpdate['formType'],
    formId: string
  ): Promise<FormUpdateResponse<T>> {
    const key = `${formType}_${formId}`;
    const lastSyncTime = this.lastSyncTimes.get(key);

    const params = new URLSearchParams({
      formType,
      formId,
    });
    if (lastSyncTime) {
      params.append('lastSyncTime', lastSyncTime);
    }

    const response = await authApi<CollaborationUpdatesResponse<T>>(
      `/collaboration/forms/updates?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'X-Last-Sync-Time': lastSyncTime || '',
        },
      }
    ) as CollaborationUpdatesResponse<T> | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch form updates');
    }

    const lastSync = response.lastSyncTime || new Date().toISOString();
    const updates = response.updates;

    if (updates) {
      return {
        hasUpdates: true,
        updates,
        lastSyncTime: lastSync,
      };
    }

    return {
      hasUpdates: false,
      lastSyncTime: lastSync,
    };
  }

  /**
   * Get all form fields for initial load
   * This matches the requirement's assumption of a GET request
   */
  async getFormFields<T = unknown>(
    formType: FormUpdate['formType'],
    formId: string
  ): Promise<T> {
    const response = await authApi<CollaborationFormFieldsResponse<T>>(
      `/collaboration/forms/${formType}/${formId}`
    ) as CollaborationFormFieldsResponse<T> | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch form fields');
    }

    const data =
      response.data ??
      response.form ??
      response.formData ??
      response.fields;

    if (data !== undefined) {
      return data;
    }

    // Fallback for development mocks
    return {} as T;
  }

  /**
   * Manual refresh - force a single update check
   */
  async manualRefresh<T = unknown>(
    formType: FormUpdate['formType'],
    formId: string
  ): Promise<FormUpdateResponse<T>> {
    console.log(`[CollaborationService] Manual refresh for ${formType}_${formId}`);
    return this.fetchFormUpdates<T>(formType, formId);
  }
  /**
   * Mock update generator for development
   * Remove this when backend is ready
   */
}

export const collaborationService = new CollaborationService();
